"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const Stats = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const stats = [
    {
      value: t("stats.stat1Value"),
      label: t("stats.stat1Label"),
    },
    {
      value: t("stats.stat2Value"),
      label: t("stats.stat2Label"),
    },
    {
      value: t("stats.stat3Value"),
      label: t("stats.stat3Label"),
    },
    {
      value: t("stats.stat4Value"),
      label: t("stats.stat4Label"),
    },
    {
      value: t("stats.stat5Value"),
      label: t("stats.stat5Label"),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [stats.length]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && activeIndex < stats.length - 1) {
        setActiveIndex(activeIndex + 1);
      } else if (diff < 0 && activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    }
    touchStartX.current = null;
  };

  return (
    <section className="bg-deep-blue py-14 px-6 lg:px-8">
      <div className="max-w-[1140px] mx-auto">
        {/* Desktop grid */}
        <div className="hidden lg:grid grid-cols-5 gap-6">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-heading font-bold text-4xl lg:text-5xl text-white mb-2">
                {stat.value}
              </div>
              <p className="font-body text-xs text-white/70 leading-relaxed">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile slider */}
        <div
          className="lg:hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="w-full flex-shrink-0 text-center px-4"
                >
                  <div className="font-heading font-bold text-4xl text-white mb-2">
                    {stat.value}
                  </div>
                  <p className="font-body text-xs text-white/70 leading-relaxed">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-6">
            {stats.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === activeIndex
                    ? "bg-white w-6"
                    : "bg-white/40 hover:bg-white/60"
                }`}
                aria-label={`Go to stat ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
